import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminService } from '../services/authService';
import { 
  UsersIcon, 
  BuildingOfficeIcon, 
  ChartBarIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';

const getActivityIcon = (action) => {
  const style = {
    backgroundColor: action.includes('LOGIN') ? '#10b981' : 
                     action.includes('CREATED') ? 'var(--primary-600)' :
                     action.includes('UPDATED') ? '#f59e0b' :
                     action.includes('DELETED') ? '#ef4444' : 
                     '#6b7280',
    width: '2rem',
    height: '2rem',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    fontWeight: '600',
    color: 'white',
    flexShrink: 0
  };
  
  return (
    <div style={style}>
      {action.charAt(0)}
    </div>
  );
};

function Dashboard() {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Check if user has admin privileges before calling admin endpoints
        if (user && (user.is_admin || user.is_super_admin)) {
          const data = await adminService.getDashboard();
          setDashboardData(data);
        } else {
          // For non-admin users, provide a basic dashboard
          setDashboardData({
            user_stats: {
              total_users: 0,
              active_users: 0
            },
            recent_activities: [],
            is_regular_user: true
          });
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        // Fallback to a basic dashboard for non-admin users
        setDashboardData({
          user_stats: {
            total_users: 0,
            active_users: 0
          },
          recent_activities: [],
          error: 'Admin dashboard not accessible',
          is_fallback: true
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '2rem'
      }}>
        <div style={{
          width: '2rem',
          height: '2rem',
          border: '2px solid var(--gray-300)',
          borderTop: '2px solid var(--primary-600)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
      </div>
    );
  }

  const isSuper = user?.is_super_admin;
  const isAdmin = user?.is_admin || user?.is_super_admin;
  const stats = isSuper ? dashboardData?.global_stats : dashboardData?.tenant_stats;
  const activities = dashboardData?.recent_activities || [];
  const isRegularUser = dashboardData?.is_regular_user || dashboardData?.is_fallback;

  // For non-admin users, show a different dashboard
  if (isRegularUser) {
    return (
      <div>
        {/* Breadcrumb Navigation */}
        <nav className="breadcrumb">
          <span className="breadcrumb-current">Dashboard</span>
        </nav>

        {/* Welcome Card */}
        <div className="card">
          <div style={{
            marginBottom: '1.5rem',
            paddingBottom: '1.5rem',
            borderBottom: '3px solid #EF4444'
          }}>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              Welcome to Marketeer
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Welcome back, {user?.first_name}! 👋
            </p>
          </div>

          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚀</div>
            <p style={{ color: 'var(--gray-600)', marginBottom: '2rem', maxWidth: '600px', margin: '0 auto 2rem auto' }}>
              Explore our powerful marketing automation tools. Navigate through the menu to access companies, projects, campaigns, and AI-powered features.
            </p>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '1rem',
              marginTop: '2rem'
            }}>
              <div style={{ padding: '1.5rem', backgroundColor: 'var(--primary-50)', borderRadius: '8px', border: '1px solid var(--primary-100)' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🏢</div>
                <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--gray-900)' }}>Companies</h3>
                <p style={{ margin: 0, color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                  Organize your business entities and their marketing activities
                </p>
              </div>
              
              <div style={{ padding: '1.5rem', backgroundColor: 'var(--success-50)', borderRadius: '8px', border: '1px solid var(--success-100)' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📊</div>
                <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--gray-900)' }}>Projects</h3>
                <p style={{ margin: 0, color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                  Manage marketing projects and track their progress
                </p>
              </div>
              
              <div style={{ padding: '1.5rem', backgroundColor: 'var(--warning-50)', borderRadius: '8px', border: '1px solid var(--warning-100)' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎯</div>
                <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--gray-900)' }}>Campaigns</h3>
                <p style={{ margin: 0, color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                  Create and monitor marketing campaigns with AI insights
                </p>
              </div>
              
              <div style={{ padding: '1.5rem', backgroundColor: 'var(--info-50)', borderRadius: '8px', border: '1px solid var(--info-100)' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🤖</div>
                <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--gray-900)' }}>AI Tools</h3>
                <p style={{ margin: 0, color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                  Leverage AI agents for content creation and analytics
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <span className="breadcrumb-current">Marketing Command Center</span>
      </nav>

      {/* Dashboard Card */}
      <div className="card">
        <div style={{
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <h2 style={{
            color: 'var(--gray-900)',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            margin: '0 0 0.25rem 0'
          }}>
            Marketing Command Center
          </h2>
          <p style={{
            color: 'var(--gray-600)',
            margin: 0,
            fontSize: '0.875rem'
          }}>
            Welcome back, {user?.first_name}! 👋
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: 'var(--space-6)',
          marginBottom: 'var(--space-6)'
        }}>
          {isSuper ? (
            <>
              <div style={{ padding: '1.5rem', backgroundColor: 'var(--gray-50)', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    backgroundColor: 'var(--primary-100)',
                    color: 'var(--primary-600)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 'var(--space-4)'
                  }}>
                    <BuildingOfficeIcon style={{ width: 'var(--space-6)', height: 'var(--space-6)' }} />
                  </div>
                  <div>
                    <div style={{
                      color: 'var(--gray-600)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-medium)',
                      marginBottom: 'var(--space-1)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.025em'
                    }}>Total Tenants</div>
                    <div style={{
                      color: 'var(--gray-900)',
                      fontSize: 'var(--font-size-4xl)',
                      fontWeight: 'var(--font-extrabold)',
                      lineHeight: 'var(--leading-tight)'
                    }}>{stats?.total_tenants || 0}</div>
                  </div>
                </div>
              </div>
              
              <div style={{ padding: '1.5rem', backgroundColor: 'var(--gray-50)', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    backgroundColor: 'var(--primary-100)',
                    color: 'var(--primary-600)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 'var(--space-4)'
                  }}>
                    <BuildingOfficeIcon style={{ width: 'var(--space-6)', height: 'var(--space-6)' }} />
                  </div>
                  <div>
                    <div style={{
                      color: 'var(--gray-600)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-medium)',
                      marginBottom: 'var(--space-1)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.025em'
                    }}>Active Tenants</div>
                    <div style={{
                      color: 'var(--gray-900)',
                      fontSize: 'var(--font-size-4xl)',
                      fontWeight: 'var(--font-extrabold)',
                      lineHeight: 'var(--leading-tight)'
                    }}>{stats?.active_tenants || 0}</div>
                  </div>
                </div>
              </div>
              
              <div style={{ padding: '1.5rem', backgroundColor: 'var(--gray-50)', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    backgroundColor: 'var(--primary-100)',
                    color: 'var(--primary-600)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 'var(--space-4)'
                  }}>
                    <UsersIcon style={{ width: 'var(--space-6)', height: 'var(--space-6)' }} />
                  </div>
                  <div>
                    <div style={{
                      color: 'var(--gray-600)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-medium)',
                      marginBottom: 'var(--space-1)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.025em'
                    }}>Total Users</div>
                    <div style={{
                      color: 'var(--gray-900)',
                      fontSize: 'var(--font-size-4xl)',
                      fontWeight: 'var(--font-extrabold)',
                      lineHeight: 'var(--leading-tight)'
                    }}>{stats?.total_users || 0}</div>
                  </div>
                </div>
              </div>
              
              <div style={{ padding: '1.5rem', backgroundColor: 'var(--gray-50)', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    backgroundColor: 'var(--primary-100)',
                    color: 'var(--primary-600)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 'var(--space-4)'
                  }}>
                    <UsersIcon style={{ width: 'var(--space-6)', height: 'var(--space-6)' }} />
                  </div>
                  <div>
                    <div style={{
                      color: 'var(--gray-600)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-medium)',
                      marginBottom: 'var(--space-1)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.025em'
                    }}>Active Users</div>
                    <div style={{
                      color: 'var(--gray-900)',
                      fontSize: 'var(--font-size-4xl)',
                      fontWeight: 'var(--font-extrabold)',
                      lineHeight: 'var(--leading-tight)'
                    }}>{stats?.active_users || 0}</div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={{ padding: '1.5rem', backgroundColor: 'var(--gray-50)', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    backgroundColor: 'var(--primary-100)',
                    color: 'var(--primary-600)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 'var(--space-4)'
                  }}>
                    <UsersIcon style={{ width: 'var(--space-6)', height: 'var(--space-6)' }} />
                  </div>
                  <div>
                    <div style={{
                      color: 'var(--gray-600)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-medium)',
                      marginBottom: 'var(--space-1)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.025em'
                    }}>Total Users</div>
                    <div style={{
                      color: 'var(--gray-900)',
                      fontSize: 'var(--font-size-4xl)',
                      fontWeight: 'var(--font-extrabold)',
                      lineHeight: 'var(--leading-tight)'
                    }}>{stats?.total_users || 0}</div>
                  </div>
                </div>
              </div>
              
              <div style={{ padding: '1.5rem', backgroundColor: 'var(--gray-50)', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    backgroundColor: 'var(--primary-100)',
                    color: 'var(--primary-600)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 'var(--space-4)'
                  }}>
                    <UsersIcon style={{ width: 'var(--space-6)', height: 'var(--space-6)' }} />
                  </div>
                  <div>
                    <div style={{
                      color: 'var(--gray-600)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-medium)',
                      marginBottom: 'var(--space-1)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.025em'
                    }}>Active Users</div>
                    <div style={{
                      color: 'var(--gray-900)',
                      fontSize: 'var(--font-size-4xl)',
                      fontWeight: 'var(--font-extrabold)',
                      lineHeight: 'var(--leading-tight)'
                    }}>{stats?.active_users || 0}</div>
                  </div>
                </div>
              </div>
              
              <div style={{ padding: '1.5rem', backgroundColor: 'var(--gray-50)', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{
                    width: '3rem',
                    height: '3rem',
                    backgroundColor: 'var(--primary-100)',
                    color: 'var(--primary-600)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 'var(--space-4)'
                  }}>
                    <UsersIcon style={{ width: 'var(--space-6)', height: 'var(--space-6)' }} />
                  </div>
                  <div>
                    <div style={{
                      color: 'var(--gray-600)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-medium)',
                      marginBottom: 'var(--space-1)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.025em'
                    }}>Admin Users</div>
                    <div style={{
                      color: 'var(--gray-900)',
                      fontSize: 'var(--font-size-4xl)',
                      fontWeight: 'var(--font-extrabold)',
                      lineHeight: 'var(--leading-tight)'
                    }}>{stats?.admin_users || 0}</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div style={{ 
          paddingTop: '1.5rem',
          borderTop: '1px solid var(--gray-200)'
        }}>
          <h3 style={{
            color: 'var(--gray-900)',
            fontSize: 'var(--font-size-xl)',
            fontWeight: 'var(--font-semibold)',
            marginBottom: 'var(--space-4)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>
            <ClockIcon style={{ width: '1.25rem', height: '1.25rem' }} />
            Recent Activity
          </h3>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            {activities.length > 0 ? (
              activities.map((activity) => (
                <div key={activity.id} style={{
                  display: 'flex',
                  alignItems: 'start',
                  gap: '1rem',
                  padding: '1rem',
                  backgroundColor: 'var(--gray-50)',
                  borderRadius: '8px'
                }}>
                  {getActivityIcon(activity.action)}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: '600',
                      color: 'var(--gray-900)',
                      marginBottom: '0.25rem'
                    }}>{activity.action.replace(/_/g, ' ')}</div>
                    <div style={{
                      color: 'var(--gray-600)',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem'
                    }}>{activity.details}</div>
                    <div style={{
                      color: 'var(--gray-500)',
                      fontSize: '0.75rem'
                    }}>
                      {format(new Date(activity.created_at), 'PPp')}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-500)' }}>
                No recent activity
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;